import {
  ADDRESS,
  CONTACT,
  DEFAULT_OG_IMAGE,
  FOUNDER_SAME_AS,
  LOGO_URL,
  RELATED_BRAND_URL,
  SITE_ORIGIN,
  SITE_NAME,
  SOCIAL_SAME_AS,
} from "@/seo/siteConfig";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}#organization`,
  name: SITE_NAME,
  alternateName: "Organisatieopstellingen by Marthe van der Molen",
  description:
    "Systemisch werk voor organisaties: organisatieopstellingen, teamcoaching en leergang systemisch leiderschap in Noord-Holland en heel Nederland.",
  url: `${SITE_ORIGIN}/`,
  logo: LOGO_URL,
  email: CONTACT.email,
  telephone: CONTACT.phone,
  address: {
    "@type": "PostalAddress",
    addressLocality: ADDRESS.locality,
    addressRegion: ADDRESS.region,
    addressCountry: ADDRESS.country,
  },
  sameAs: [...SOCIAL_SAME_AS],
  founder: {
    "@type": "Person",
    name: "Marthe van der Molen",
    jobTitle: "Systemisch coach en organisatieadviseur",
    url: `${SITE_ORIGIN}/over`,
    worksFor: { "@id": `${SITE_ORIGIN}#organization` },
    sameAs: [...FOUNDER_SAME_AS],
    knowsAbout: [
      "Organisatieopstellingen",
      "Systemische teamcoaching",
      "Systemisch leiderschap",
      "Systemisch werk",
    ],
  },
  subOrganization: {
    "@type": "Organization",
    name: "Systemique",
    url: RELATED_BRAND_URL,
    description: "Particulier aanbod: familieopstellingen en agenda op systemique.nl",
  },
} as const;

const website = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}#website`,
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  inLanguage: "nl-NL",
  publisher: { "@id": `${SITE_ORIGIN}#organization` },
} as const;

const professionalService = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_ORIGIN}#professional-service`,
  name: "Organisatieopstellingen, systemische coaching voor organisaties",
  alternateName: SITE_NAME,
  image: DEFAULT_OG_IMAGE,
  description:
    "Organisatieopstellingen, systemische teamcoaching en systemische leergang voor leiders in Noord-Holland",
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    addressLocality: ADDRESS.locality,
    addressRegion: ADDRESS.region,
    addressCountry: ADDRESS.country,
  },
  telephone: CONTACT.phone,
  url: `${SITE_ORIGIN}/`,
  parentOrganization: { "@id": `${SITE_ORIGIN}#organization` },
  areaServed: [
    { "@type": "City", name: "Castricum" },
    { "@type": "City", name: "Amsterdam" },
    { "@type": "City", name: "Haarlem" },
    { "@type": "City", name: "Zaandam" },
    { "@type": "City", name: "Alkmaar" },
    { "@type": "AdministrativeArea", name: "Noord-Holland" },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    bestRating: "5",
    ratingCount: "3",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Systemische diensten",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Systemische coaching",
          description: "Persoonlijke en zakelijke coaching met systemische benadering",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Familieopstellingen",
          description: "Familieopstellingen voor particulieren in Noord-Holland",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Organisatieopstellingen",
          description: "Organisatieopstellingen voor teams en bedrijven",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Teamcoaching",
          description: "Systemische teamcoaching voor organisaties",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Course",
          name: "Systemische leergang voor leiders | Duiken in de Onderstroom",
          description: "3-daagse leergang systemisch leiderschap voor leiders en adviseurs",
          url: `${SITE_ORIGIN}/leergang-onderstroom`,
        },
      },
    ],
  },
} as const;

const article = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Wat is systemisch werk? Uitleg en toepassingen",
  description:
    "Systemisch werk is een methodiek die kijkt naar de onderstroom in systemen. Uitleg over familieopstellingen, organisatieopstellingen en systemische coaching.",
  author: {
    "@type": "Person",
    name: "Marthe van der Molen",
    url: `${SITE_ORIGIN}/over`,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
  },
  datePublished: "2024-01-01",
  dateModified: "2026-09-13",
  mainEntityOfPage: `${SITE_ORIGIN}/`,
  keywords:
    "systemisch werk, systemische coaching, familieopstellingen, organisatieopstellingen, systemisch perspectief, onderstroom, systemische dynamieken, systemisch leiderschap",
  articleBody:
    "Systemisch werk is een methodiek die kijkt naar de onderstroom en dynamieken in systemen zoals teams en organisaties. Het maakt onzichtbare patronen, loyaliteiten en verstrikkingen zichtbaar. Op organisatieopstellingen.com richten we ons op zakelijke vraagstukken: organisatieopstellingen, systemische teamcoaching en systemische leergang voor leiders (Duiken in de Onderstroom).",
} as const;

const howTo = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Hoe werkt systemisch werk?",
  description: "Stap-voor-stap uitleg van hoe systemisch werk werkt en wat je kunt verwachten bij een organisatieopstelling",
  step: [
    {
      "@type": "HowToStep",
      name: "Kennismaking en intake",
      text: `Je neemt contact op via organisatieopstellingen.com/contact voor een kennismakingsgesprek. We bespreken jouw organisatie- of teamvraag en bepalen welke vorm past: organisatieopstelling, teamcoaching of systemische leergang voor leiders. Contact: ${CONTACT.email} of 06-12991453.`,
    },
    {
      "@type": "HowToStep",
      name: "Deelname aan opstelling of coaching",
      text: "Je neemt deel aan een opstellingenmiddag in Het Atelier, Uitgeest, 1-op-1 coaching in Castricum, of teamcoaching op locatie. Tijdens de sessie worden systemische dynamieken zichtbaar gemaakt door representanten of symbolen te gebruiken.",
    },
    {
      "@type": "HowToStep",
      name: "Inzicht en integratie",
      text: "Je krijgt inzicht in patronen en dynamieken in jouw systeem. Deze inzichten kun je direct toepassen in je dagelijks leven of werk. Systemisch werk geeft vaak razendsnel inzicht en creëert ruimte voor duurzame verandering.",
    },
  ],
  totalTime: "PT3H",
  supply: [
    {
      "@type": "HowToSupply",
      name: "Strippenkaart of los ticket voor opstellingenmiddagen",
    },
  ],
  tool: [
    {
      "@type": "HowToTool",
      name: "Systemische opstellingen",
    },
  ],
} as const;

const definedTermSet = {
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  name: "Systemisch werk terminologie",
  description: "Uitleg van termen en begrippen gerelateerd aan systemisch werk",
  hasDefinedTerm: [
    {
      "@type": "DefinedTerm",
      name: "Systemisch werk",
      description:
        "Een methodiek die kijkt naar de onderstroom en dynamieken in systemen zoals families, teams en organisaties. Het maakt onzichtbare patronen, loyaliteiten en verstrikkingen zichtbaar.",
      inDefinedTermSet: SITE_ORIGIN,
    },
    {
      "@type": "DefinedTerm",
      name: "Familieopstelling",
      description:
        "Een systemische methode waarbij familiepatronen en dynamieken zichtbaar worden gemaakt door representanten of symbolen te gebruiken. Helpt bij persoonlijke ontwikkeling en inzicht in familiepatronen.",
      inDefinedTermSet: SITE_ORIGIN,
    },
    {
      "@type": "DefinedTerm",
      name: "Organisatieopstelling",
      description:
        "Een systemische methode die dynamieken in teams en organisaties zichtbaar maakt, zoals hoog verloop, conflicten of samenwerkingsproblemen. Helpt bij organisatieontwikkeling.",
      inDefinedTermSet: SITE_ORIGIN,
    },
    {
      "@type": "DefinedTerm",
      name: "Onderstroom",
      description:
        "De onzichtbare dynamieken, patronen en krachten in een systeem die gedrag en resultaten beïnvloeden. Systemisch werk maakt de onderstroom zichtbaar.",
      inDefinedTermSet: SITE_ORIGIN,
    },
    {
      "@type": "DefinedTerm",
      name: "Systemische coaching",
      description:
        "Coaching die gebruik maakt van systemisch werk om individuen, teams en organisaties te begeleiden. Helpt bij het herkennen en doorbreken van systemische patronen.",
      inDefinedTermSet: SITE_ORIGIN,
    },
    {
      "@type": "DefinedTerm",
      name: "Representant",
      description:
        "Iemand die tijdens een opstelling een rol inneemt (bijvoorbeeld een familielid of teamlid) om dynamieken zichtbaar te maken.",
      inDefinedTermSet: SITE_ORIGIN,
    },
  ],
} as const;

const primaryService = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_ORIGIN}#service-organisatieopstellingen`,
  name: "Organisatieopstellingen",
  description:
    "Organisatieopstellingen voor teams, leiders en organisaties in Noord-Holland en de Metropoolregio Amsterdam.",
  serviceType: "Organisatieopstellingen",
  provider: { "@id": `${SITE_ORIGIN}#organization` },
  url: `${SITE_ORIGIN}/organisatieopstellingen`,
  areaServed: [
    { "@type": "AdministrativeArea", name: "Noord-Holland" },
    { "@type": "Country", name: "Nederland" },
  ],
} as const;

/** Volledige JSON-LD documenten voor de homepage (elk object is standalone + @context). */
export const HOME_PAGE_JSON_LD: Record<string, unknown>[] = [
  organization as unknown as Record<string, unknown>,
  website as unknown as Record<string, unknown>,
  professionalService as unknown as Record<string, unknown>,
  primaryService as unknown as Record<string, unknown>,
  article as unknown as Record<string, unknown>,
  howTo as unknown as Record<string, unknown>,
  definedTermSet as unknown as Record<string, unknown>,
];
